import { useEffect, useRef, useState } from 'react';
import useFetchOnUserActionUsingSDS from './useFetchOnUserActionUsingSDS';
import { SNOW_VALIDATION_SERVICE_ENDPOINT } from 'src/config';
import { ValidationExecutionStatus, ValidationReport, ValidationResult } from 'src/@types/validationTypes';

interface ValidationStatusResponse {
	validationExecution: {
		validationExecutionId: string;
		validationOverallStatus: ValidationExecutionStatus | string;
		lastUpdatedTime?: number;
		message?: string;
		validationReport?: {
			failedValidations?: number | null;
			passedValidations?: number | null;
			totalValidations?: number | null;
		};
		validationResults?: Array<{
			errorMessage?: string | null;
			status?: string | null;
			validationId?: string | null;
			validationName?: string | null;
			workflowId?: string | null;
		}>;
	};
}

interface ValidationPollingState {
	validationExecutionId: string;
	status: ValidationExecutionStatus | string;
	message?: string;
	isPolling: boolean;
	validationReport?: ValidationReport;
	validationResults?: ValidationResult[];
}

export const useValidationPolling = (
	entityName: string,
	validationExecutionId: string | undefined,
	workflowType: string,
	onValidationPassed?: (entityName: string, workflowType: string) => void
) => {
	const [validationState, setValidationState] = useState<ValidationPollingState | null>(null);
	const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
	const onValidationPassedRef = useRef(onValidationPassed);
	const workflowExecutionTriggeredRef = useRef(false);

	useEffect(() => {
		onValidationPassedRef.current = onValidationPassed;
	}, [onValidationPassed]);

	const {
		result: { data: validationStatusData },
		onClickCB: fetchValidationStatus,
	} = useFetchOnUserActionUsingSDS<ValidationStatusResponse>();

	const isTerminalState = (status: string) => {
		return [
			ValidationExecutionStatus.VALIDATION_PASSED,
			ValidationExecutionStatus.VALIDATION_FAILED,
			ValidationExecutionStatus.VALIDATION_FATAL,
			ValidationExecutionStatus.VALID,
			ValidationExecutionStatus.INVALID,
			ValidationExecutionStatus.FAILED,
		].includes(status as ValidationExecutionStatus);
	};

	const parseResponse = (data: ValidationStatusResponse) => {
		const execution = data.validationExecution;
		
		const validationReport = execution.validationReport ? {
			failedValidations: execution.validationReport.failedValidations ?? 0,
			passedValidations: execution.validationReport.passedValidations ?? 0,
			totalValidations: execution.validationReport.totalValidations ?? 0,
		} : undefined;

		const validationResults = execution.validationResults
			?.filter(item => item.validationName || item.errorMessage || item.status)
			.map(item => ({
				errorMessage: item.errorMessage ?? undefined,
				status: item.status ?? undefined,
				validationId: item.validationId ?? undefined,
				validationName: item.validationName ?? undefined,
				workflowId: item.workflowId ?? undefined,
			}));

		return { validationReport, validationResults };
	};

	const pollValidationStatus = async () => {
		if (!validationExecutionId) return;

		await fetchValidationStatus({
			path: `/validations/execution/${validationExecutionId}`,
			method: 'GET',
			url: SNOW_VALIDATION_SERVICE_ENDPOINT,
		});
	};

	useEffect(() => {
		if (validationStatusData) {
			
			const execution = validationStatusData.validationExecution;
			const status = execution.validationOverallStatus;
			const isTerminal = isTerminalState(status);
			const { validationReport, validationResults } = parseResponse(validationStatusData);

			setValidationState({
				validationExecutionId: execution.validationExecutionId,
				status,
				message: execution.message,
				isPolling: !isTerminal,
				validationReport,
				validationResults,
			});

			if (isTerminal) {
				if (pollingIntervalRef.current) {
					clearInterval(pollingIntervalRef.current);
					pollingIntervalRef.current = null;
				}

				if ((status === ValidationExecutionStatus.VALIDATION_PASSED || status === ValidationExecutionStatus.VALID) && 
					onValidationPassedRef.current && 
					!workflowExecutionTriggeredRef.current) {
					workflowExecutionTriggeredRef.current = true;
					onValidationPassedRef.current(entityName, workflowType);
				} 
			} else {
				if (!pollingIntervalRef.current) {
					pollingIntervalRef.current = setInterval(() => {
						pollValidationStatus();
					}, 60000);
				}
			}
		}
	}, [validationStatusData, entityName, workflowType]);

	useEffect(() => {
		if (validationExecutionId) {
			workflowExecutionTriggeredRef.current = false;
			pollValidationStatus();
		} else {
			setValidationState(null);
			workflowExecutionTriggeredRef.current = false;
		}
		
		return () => {
			if (pollingIntervalRef.current) {
				clearInterval(pollingIntervalRef.current);
				pollingIntervalRef.current = null;
			}
		};
	}, [validationExecutionId]);

	return validationState;
};
